import { useLocale } from 'containers/App/LocaleContext';
import type { FormikProps } from 'formik';
import { useEffect, useMemo } from 'react';
import type { FC } from 'react';

import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import { FormikSwitchField } from 'components/FormikSwitchField/FormikSwitchField';
import { getBooleanOptionValue } from 'utils/form/getBooleanOptionValue';
import { isValidCidr } from 'utils/formValidation/validations';
import type { UnicastFormValues } from './UnicastFormValues';

interface Props {
  formik: FormikProps<UnicastFormValues>;
}

export const IpModeSwitch: FC<Props> = (props) => {
  const msg = useLocale();

  const ipModeIp = useMemo(
    () =>
      !isValidCidr(props.formik.values.startingPoint) &&
      !isValidCidr(props.formik.values.destinationPoint),
    [props.formik.values.startingPoint, props.formik.values.destinationPoint],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies(props.formik.setFieldValue): stable
  useEffect(() => {
    if (ipModeIp) {
      props.formik.setFieldValue('hostMode', true);
    }
  }, [ipModeIp]);

  const label = (
    <>
      {msg.canvasGraph.graphSidePanel.pathlookup.unicast.pathLookupMode}
      <TooltipIcon
        content={
          <div className='text-xs'>
            <div className='font-semibold'>
              {msg.canvasGraph.graphSidePanel.pathlookup.unicast.hostMode}
            </div>
            <div>
              {
                msg.canvasGraph.graphSidePanel.pathlookup.unicast
                  .hostModeTooltip
              }
            </div>
            <div className='mt-4 font-semibold'>
              {msg.canvasGraph.graphSidePanel.pathlookup.unicast.networkMode}
            </div>
            <div>
              {
                msg.canvasGraph.graphSidePanel.pathlookup.unicast
                  .networkModeTooltip
              }
            </div>
          </div>
        }
        iconProps={{
          inline: true,
          iconStyle: 'solid',
        }}
        size='sm'
        className='ml-2'
      />
    </>
  );

  return (
    <FormikSwitchField
      name='hostMode'
      disabled={ipModeIp}
      label={label}
      formik={props.formik}
      options={[
        {
          value: false,
          label: msg.canvasGraph.graphSidePanel.pathlookup.unicast.networkMode,
        },
        {
          value: true,
          label: msg.canvasGraph.graphSidePanel.pathlookup.unicast.hostMode,
        },
      ]}
      getOptionValue={getBooleanOptionValue}
      size='small'
    />
  );
};
